import React, { useEffect, useReducer } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import StickyHeaderDataTable from "../components/shared/sticky-header-data-table";

import { MetricsProspectsFilters } from "../components/metrics-prospects/metrics-prospects-filters";
import PageHeaderRowSearch from "../components/shared/page-header-row-search";
import ai from "../helpers/axios";
import currencyFormatter from "../helpers/currencyFormatter";
import Loader from "../components/loader";
import { EllipsisCellWithTooltip } from "../components/shared/table/ellipsis-cell-with-tooltip";

const columns = [
  {
    name: "Name",
    selector: "prospectName",
    sortable: true,
    cell: row => <EllipsisCellWithTooltip text={row.prospectName}/>,
  },
  {
    name: "Completed Trips",
    selector: "completedTrips",
    right: true,
    sortable: true,
  },
  {
    name: " Total Spend",
    selector: "totalSpend",
    cell: (row) => {
      if (
        (row?.totalSpend || row.totalSpend === 0) &&
        !isNaN(row?.totalSpend)
      ) {
        return currencyFormatter.format(row.totalSpend);
      }

      return "-";
    },
    right: true,
    sortable: true,
  },
  {
    name: " Flt. Hours",
    selector: "flightHours",
    right: true,
    sortable: true,
    cell: (row) => {
      if (!row.flightHours) {
        return "-";
      }

      const duration = moment.duration(row.flightHours, 'hours');
      const hours = String(Math.floor(duration.asHours())).padStart(2, '0');
      const minutes = String(duration.minutes()).padStart(2, '0');

      return `${hours}h ${minutes}m`;
    },
  },
  {
    name: " First Flight",
    selector: "firstFlight",
    cell: (row) =>
      row.firstFlight ?
        moment(row.firstFlight).format("MMM Do, YYYY")
        : "-",
    right: true,
    sortable: true,
  },
  {
    name: " Owner",
    selector: "owner",
    right: true,
    sortable: true,
  },
];

const buildQuery = ({
  dates = [],
  search = "",
  currentPage,
  countPerPage,
  sortBy,
  sortDirection,
  isExport,
}) => {
  const params = new URLSearchParams();

  if (search.trim() !== "") {
    params.append("search", search.trim());
  }

  const [startDate, endDate] = dates;

  Object.entries({
    startDate,
    endDate,
    currentPage,
    countPerPage,
    sortBy,
    sortDirection,
    isExport,
  }).forEach(([key, value]) => {
    if (value) {
      params.append(key, value);
    }
  });

  const query = params.toString();

  return query ? `?${query}` : "";
};

const initialState = {
  isLoading: false,
  isExporting: false,

  dates: [],
  datesChangedAfterLastRequest: false,
  search: "",

  // table data
  rows: [],
  totalItems: 0,
  currentPage: 1,
  countPerPage: 10,
  sortBy: "completedTrips",
  sortDirection: "desc",
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "datesRangeChanged": {
      return {
        ...state,
        dates: action.dates,
        datesChangedAfterLastRequest: true,
        currentPage: 1,
      };
    }

    case "search": {
      return {
        ...state,
        search: action.value,
        currentPage: 1,
      };
    }

    case "newMetricsDownloaded": {
      return {
        ...state,
        ...action.payload,
        isLoading: false,
        datesChangedAfterLastRequest: false,
      };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.currentRowsPerPage,
        currentPage: 1,
      };
    }

    case "sortingChanged": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
      };
    }

    default:
      break;
  }
};

const MetricsProspects = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  useEffect(() => {
    document.title = "Metrics - Top Prospects | evoJets HQ";
  }, []);

  const getData = async ({ isExport = false } = {}) => {
    dispatch({ type: "fieldChanged", field: isExport ? "isExporting" : "isLoading", value: true });

    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    const query = buildQuery({
      ...state,
      isExport,
    });

    try {
      let response = await ai.auth(token).get(`/api/metrics/prospects${query}`,
        {
          ...(isExport ? { responseType: "blob" } : {}),
        });

      if (isExport) {
        try {
          const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
          const url = URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.download = "prospect-metrics.csv";
          link.click();

          setTimeout(() => URL.revokeObjectURL(url), 100);

          toast.success("Prospects metrics downloaded");
        } catch (e) {
          toast.error("Error downloading metrics");
        }

        dispatch({
          type: "fieldChanged",
          field: "isExporting",
          value: false,
        });
      } else {
        if (!response?.data?.success) {
          throw Error();
        }

        dispatch({ type: "newMetricsDownloaded", payload: response.data.metrics });

        toast.success("Prospects metrics loaded");
      }
    } catch (e) {
      console.error(e);

      dispatch({
        type: "fieldChanged",
        field: isExport ? "isExporting" : "isLoading",
        value: false,
      });

      toast.error(`Error ${isExport ? "exporting" : "getting"} prospects metrics`);
    }

    return {};
  };

  const handleExport = () => {
    getData({ isExport: true });
  };

  useEffect(() => {
    getData();
  }, [
    state.currentPage,
    state.countPerPage,
    state.sortBy,
    state.sortDirection,
  ]);

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "fieldChanged",
      field: "currentPage",
      value: page,
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      currentRowsPerPage,
    });
  };

  const handleSort = (column, sortDirection) => {
    dispatch({
      type: "sortingChanged",
      payload: {
        sortBy: column.selector,
        sortDirection,
      },
    });
  };

  return <>
    <PageHeaderRowSearch h1="Top Prospects"/>
    <MetricsProspectsFilters
      parentState={state}
      parentDispatch={dispatch}
      getData={getData}
      handleExport={handleExport}
    />

    <div
      className="server-pagination-table-container metrics-table-container data-table-compact-view shadow-none mt-2">
      <StickyHeaderDataTable
        className="metrics-table"
        columns={columns}
        data={state.rows}
        persistTableHead
        progressPending={state.isLoading}
        progressComponent={<Loader additionalClasses="metrics-table-loader"/>}

        // pagination
        pagination
        paginationServer
        paginationTotalRows={state.totalItems}
        paginationPerPage={state.countPerPage}
        paginationDefaultPage={state.currentPage}
        paginationRowsPerPageOptions={[5, 10, 25, 50, 100, 150, 200]}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        paginationResetDefaultPage={false}

        // sorting
        sortServer
        onSort={handleSort}
        defaultSortField={state.sortBy}
        sortDirection={state.sortDirection}
      />
    </div>
  </>;
};

export default MetricsProspects;
